import { DestinationCategory, ActivityTypeWithDescription } from '../types/chat';

// API URLs - Use environment variables with fallback to Python backend (port 8080)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://ai-travel-920175119171.europe-west3.run.app'
    : 'http://localhost:8080');

// All endpoints now point directly to Python FastAPI backend
export const BACKEND_URL = `${API_BASE_URL}/api/travel`;
export const STATES_URL = `${API_BASE_URL}/api/states`;
export const ACTIVITY_DETAILS_URL = `${API_BASE_URL}/api/activity-details`;
export const ACTIVITY_PHOTOS_URL = `${API_BASE_URL}/api/activity-photos`;
export const ADD_DAY_ACTIVITIES_URL = `${API_BASE_URL}/api/add-day-activities`;
export const GENERATE_NEW_DAY_URL = `${API_BASE_URL}/api/generate-new-day`;
export const ADD_RESTAURANTS_URL = `${API_BASE_URL}/api/add-restaurants`;
export const DELETE_ACTIVITY_URL = `${API_BASE_URL}/api/delete-activity`;
export const MOVE_ACTIVITY_URL = `${API_BASE_URL}/api/move-activity`;
export const SAVE_TRIP_URL = `${API_BASE_URL}/api/save-trip`;
export const DESTINATION_INFO_URL = `${API_BASE_URL}/api/destination-info`;
export const WEATHER_URL = `${API_BASE_URL}/api/weather`;
export const SEARCH_HISTORY_URL = `${API_BASE_URL}/api/search-history`;
export const REFRESH_DESTINATION_IMAGES_URL = `${API_BASE_URL}/api/destinations/refresh-images`;
export const CHATS_URL = `${API_BASE_URL}/api/chats`;

// Activity types with descriptions
export const ACTIVITY_TYPES_WITH_DESCRIPTIONS: ActivityTypeWithDescription[] = [
  { value: 'cultural', label: 'Cultural', description: 'museums, history, heritage sites' },
  { value: 'food', label: 'Food', description: 'restaurants, local cuisine, food tours' },
  { value: 'nature', label: 'Nature', description: 'wildlife, gardens, natural reserves' },
  { value: 'art', label: 'Art', description: 'galleries, studios, exhibitions' },
  { value: 'shopping', label: 'Shopping', description: 'markets, malls, local crafts' },
  { value: 'nightlife', label: 'Nightlife', description: 'bars, clubs, evening entertainment' },
  { value: 'relaxation', label: 'Relaxation', description: 'spas, beaches, wellness' },
  { value: 'local_experiences', label: 'Local Experiences', description: 'authentic culture, traditions' }
];

export const ACTIVITY_TYPES = ACTIVITY_TYPES_WITH_DESCRIPTIONS.map(item => item.value);

export const TRANSPORTATION_TYPES = [
  'taxi',
  'flight',
  'train',
  'car',
  'public_transit',
  'walking',
  'bicycle',
  'bus',
  'metro'
];

export const BUDGET_CATEGORIES = [
  'low',
  'medium', 
  'high',
  'luxury'
];

export const DESTINATION_CATEGORIES: Record<string, DestinationCategory> = {
  cultural: {
    icon: '🏛️',
    label: 'Cultural Explorer',
    description: 'Historic cities & cultural treasures',
    destinations: [
      'Rome, Italy',
      'Kyoto, Japan', 
      'Istanbul, Turkey',
      'Prague, Czech Republic',
      'Fez, Morocco',
      'Athens, Greece',
      'Cusco, Peru',
      'Florence, Italy',
      'Barcelona, Spain',
      'Paris, France',
      'Jerusalem, Israel',
      'Varanasi, India',
      'Marrakech, Morocco',
      'Vienna, Austria',
      'St. Petersburg, Russia',
      'Mexico City, Mexico',
      'Cairo, Egypt',
      'Amman, Jordan',
      'Siem Reap, Cambodia',
      'Delhi, India',
      'Lisbon, Portugal',
      'Edinburgh, Scotland',
      'Budapest, Hungary',
      'Krakow, Poland',
      'Dubrovnik, Croatia',
      'Seville, Spain',
      'Valletta, Malta',
      'Luang Prabang, Laos',
      'Hanoi, Vietnam',
      'Cartagena, Colombia',
      'Oaxaca, Mexico',
      'Thimphu, Bhutan',
      'Yangon, Myanmar',
      'Samarkand, Uzbekistan',
      'Tbilisi, Georgia',
      'Yerevan, Armenia',
      'Baku, Azerbaijan',
      'Kathmandu, Nepal',
      'Lhasa, Tibet',
      'Ulaanbaatar, Mongolia'
    ]
  },
  adventure: {
    icon: '🏔️',
    label: 'Adventure Seeker',
    description: 'Thrilling destinations & outdoor activities',
    destinations: [
      'Queenstown, New Zealand',
      'Puerto Natales, Chile',
      'Interlaken, Switzerland',
      'Banff, Canada',
      'San José, Costa Rica',
      'Hobart, Australia',
      'Oslo, Norway',
      'Chamonix, France',
      'Moab, USA',
      'Punta Arenas, Chile',
      'Cortina d\'Ampezzo, Italy',
      'Whistler, Canada',
      'Fuji, Japan',
      'Arusha, Tanzania',
      'Svolvær, Norway',
      'Springdale, USA',
      'Cusco, Peru',
      'Reykjavik, Iceland',
      'Kathmandu, Nepal',
      'Zermatt, Switzerland',
      'Auckland, New Zealand',
      'Anchorage, USA',
      'Seattle, USA',
      'Yosemite Valley, USA',
      'Denver, USA',
      'Toulouse, France',
      'Christchurch, New Zealand',
      'El Calafate, Argentina',
      'Mendoza, Argentina',
      'Boulder, USA',
      'Aspen, USA',
      'Jackson Hole, USA',
      'Telluride, USA',
      'Park City, USA',
      'Lake Tahoe, USA',
      'Bend, USA',
      'Sedona, USA'
    ]
  },
  tropical: {
    icon: '🏝️',
    label: 'Beach Paradise',
    description: 'Tropical islands & coastal escapes',
    destinations: [
      'Maldives',
      'Bali, Indonesia',
      'Santorini, Greece',
      'Seychelles',
      'Maui, Hawaii',
      'Phuket, Thailand',
      'Fiji',
      'Barbados',
      'Amalfi, Italy',
      'Zanzibar, Tanzania',
      'Bora Bora, French Polynesia',
      'Puerto Princesa, Philippines',
      'Port Louis, Mauritius',
      'Cockburn Town, Turks and Caicos',
      'Mykonos, Greece',
      'Oranjestad, Aruba',
      'Avarua, Cook Islands',
      'Langkawi, Malaysia',
      'Airlie Beach, Australia',
      'Koh Samui, Thailand',
      'Honolulu, USA',
      'Papeete, French Polynesia',
      'Cabo San Lucas, Mexico',
      'Tulum, Mexico',
      'Playa del Carmen, Mexico',
      'Cancun, Mexico',
      'Kingston, Jamaica',
      'Santo Domingo, Dominican Republic',
      'Nassau, Bahamas',
      'Castries, St. Lucia',
      'St. George\'s, Grenada',
      'St. John\'s, Antigua',
      'Gustavia, St. Barts',
      'Capri, Italy',
      'Ibiza, Spain',
      'Palma, Spain',
      'Heraklion, Greece',
      'Rhodes, Greece',
      'Panaji, India',
      'Colombo, Sri Lanka',
      'Boracay, Philippines',
      'Cebu, Philippines',
      'Gili Trawangan, Indonesia',
      'Mataram, Indonesia',
      'Sorong, Indonesia',
      'Cairns, Australia',
      'Airlie Beach, Australia'
    ]
  },
  urban: {
    icon: '🌆',
    label: 'City Explorer',
    description: 'Vibrant cities & metropolitan adventures',
    destinations: [
      'Tokyo, Japan',
      'New York, USA',
      'London, England',
      'Barcelona, Spain',
      'Singapore',
      'Berlin, Germany',
      'Melbourne, Australia',
      'São Paulo, Brazil',
      'Seoul, South Korea',
      'Amsterdam, Netherlands',
      'Paris, France',
      'Hong Kong',
      'Dubai, UAE',
      'Sydney, Australia',
      'Toronto, Canada',
      'Stockholm, Sweden',
      'Vienna, Austria',
      'Tel Aviv, Israel',
      'Copenhagen, Denmark',
      'Buenos Aires, Argentina',
      'Los Angeles, USA',
      'San Francisco, USA',
      'Chicago, USA',
      'Miami, USA',
      'Las Vegas, USA',
      'Boston, USA',
      'Seattle, USA',
      'Washington DC, USA',
      'Montreal, Canada',
      'Vancouver, Canada',
      'Mexico City, Mexico',
      'Rio de Janeiro, Brazil',
      'Lima, Peru',
      'Santiago, Chile',
      'Bogotá, Colombia',
      'Madrid, Spain',
      'Lisbon, Portugal',
      'Rome, Italy',
      'Milan, Italy',
      'Venice, Italy',
      'Munich, Germany',
      'Hamburg, Germany',
      'Frankfurt, Germany',
      'Zurich, Switzerland',
      'Brussels, Belgium',
      'Dublin, Ireland',
      'Edinburgh, Scotland',
      'Oslo, Norway',
      'Helsinki, Finland',
      'Warsaw, Poland',
      'Budapest, Hungary',
      'Prague, Czech Republic',
      'Istanbul, Turkey',
      'Cairo, Egypt',
      'Cape Town, South Africa',
      'Johannesburg, South Africa',
      'Nairobi, Kenya',
      'Casablanca, Morocco',
      'Mumbai, India',
      'Delhi, India',
      'Bangalore, India',
      'Bangkok, Thailand',
      'Ho Chi Minh City, Vietnam',
      'Hanoi, Vietnam',
      'Manila, Philippines',
      'Jakarta, Indonesia',
      'Kuala Lumpur, Malaysia',
      'Shanghai, China',
      'Beijing, China',
      'Taipei, Taiwan',
      'Osaka, Japan',
      'Kyoto, Japan',
      'Auckland, New Zealand',
      'Wellington, New Zealand',
      'Perth, Australia',
      'Brisbane, Australia'
    ]
  },
  nature: {
    icon: '🌿',
    label: 'Nature Lover',
    description: 'National parks & natural wonders',
    destinations: [
      'West Yellowstone, USA',
      'El Calafate, Argentina',
      'Puerto Natales, Chile',
      'Nelspruit, South Africa',
      'Puerto Ayora, Ecuador',
      'Inverness, Scotland',
      'Banff, Canada',
      'Te Anau, New Zealand',
      'Manaus, Brazil',
      'Yosemite Valley, USA',
      'Grand Canyon Village, USA',
      'Arusha, Tanzania',
      'Anchorage, USA',
      'Cairns, Australia',
      'Bergen, Norway',
      'Port Douglas, Australia',
      'Nairobi, Kenya',
      'Maun, Botswana',
      'Calgary, Canada',
      'Reykjavik, Iceland',
      'Queenstown, New Zealand',
      'Interlaken, Switzerland',
      'Oslo, Norway',
      'West Glacier, USA',
      'Moab, USA',
      'Bryce, USA',
      'Springdale, USA',
      'Jackson, USA',
      'Gustavus, USA',
      'Healy, USA',
      'Port Angeles, USA',
      'Crescent City, USA',
      'Three Rivers, USA',
      'Twentynine Palms, USA',
      'Furnace Creek, USA',
      'Homestead, USA',
      'Jasper, Canada',
      'Waterton Park, Canada',
      'Rocky Harbour, Canada',
      'Keswick, England',
      'Interlaken, Switzerland',
      'Cortina d\'Ampezzo, Italy',
      'Plitvice, Croatia',
      'Bled, Slovenia',
      'Irkutsk, Russia',
      'Pokhara, Nepal',
      'Lukla, Nepal',
      'Lhasa, China',
      'Chengdu, China',
      'Zhangjiajie, China',
      'Fuji, Japan',
      'Tokyo, Japan',
      'Labuan Bajo, Indonesia',
      'Kota Kinabalu, Malaysia',
      'Medan, Indonesia',
      'Kuala Tembeling, Malaysia',
      'Pak Chong, Thailand',
      'Koh Samui, Thailand',
      'Ha Long, Vietnam',
      'Sapa, Vietnam',
      'Dong Hoi, Vietnam',
      'Hobart, Australia',
      'Jabiru, Australia',
      'Yulara, Australia',
      'Katoomba, Australia',
      'Te Anau, New Zealand',
      'National Park, New Zealand',
      'Motueka, New Zealand',
      'Livingstone, Zambia',
      'Arusha, Tanzania',
      'Kampala, Uganda',
      'Musanze, Rwanda',
      'Outjo, Namibia',
      'Kasane, Botswana',
      'Cape Town, South Africa',
      'Underberg, South Africa',
      'Sesriem, Namibia',
      'Swakopmund, Namibia',
      'San Pedro de Atacama, Chile',
      'Puerto Iguazu, Argentina',
      'El Calafate, Argentina',
      'Bariloche, Argentina',
      'Cuiaba, Brazil',
      'Foz do Iguacu, Brazil',
      'Barreirinhas, Brazil',
      'Fernando de Noronha, Brazil',
      'Cusco, Peru',
      'Arequipa, Peru',
      'Puerto Maldonado, Peru',
      'Quito, Ecuador',
      'Latacunga, Ecuador',
      'El Calafate, Argentina',
      'Puerto Natales, Chile',
      'Hanga Roa, Chile',
      'Puerto Madryn, Argentina'
    ]
  },
  exotic: {
    icon: '✨',
    label: 'Off the Beaten Path',
    description: 'Hidden gems & unique destinations',
    destinations: [
      'Socotra Island, Yemen',
      'Faroe Islands',
      'Raja Ampat, Indonesia',
      'Uyuni, Bolivia',
      'Easter Island, Chile',
      'Svalbard, Norway',
      'Leh, India',
      'Papua New Guinea',
      'Petropavlovsk-Kamchatsky, Russia',
      'Greenland',
      'Addis Ababa, Ethiopia',
      'San Pedro de Atacama, Chile',
      'Jan Mayen, Norway',
      'Azores, Portugal',
      'Stornoway, Scotland',
      'Moroni, Comoros',
      'Madeira, Portugal',
      'Canary Islands, Spain',
      'Cape Verde',
      'São Tomé and Príncipe',
      'Réunion Island',
      'Madagascar',
      'Port Blair, India',
      'Kavaratti, India',
      'Bhutan',
      'Myanmar',
      'Laos',
      'Mongolia',
      'Kyrgyzstan',
      'Tajikistan',
      'Uzbekistan',
      'Turkmenistan',
      'Georgia',
      'Armenia',
      'Azerbaijan',
      'Albania',
      'North Macedonia',
      'Montenegro',
      'Bosnia and Herzegovina',
      'Moldova',
      'Kosovo',
      'San Marino',
      'Liechtenstein',
      'Andorra',
      'Monaco',
      'Vatican City',
      'Lerwick, Scotland',
      'Kirkwall, Scotland',
      'Portree, Scotland',
      'Stornoway, Scotland',
      'Douglas, Isle of Man',
      'St. Helier, Channel Islands',
      'Ajaccio, France',
      'Cagliari, Italy',
      'Palermo, Italy',
      'Mykonos, Greece',
      'Rhodes, Greece',
      'Corfu, Greece',
      'Palma, Spain',
      'Mamoudzou, Mayotte',
      'Port Mathurin, Mauritius',
      'Diego Garcia, British Indian Ocean Territory',
      'West Island, Cocos Islands',
      'Flying Fish Cove, Christmas Island',
      'Kingston, Norfolk Island',
      'Lord Howe Island, Australia',
      'Kingscote, Australia',
      'Noumea, New Caledonia',
      'Port Vila, Vanuatu',
      'Nuku\'alofa, Tonga',
      'Apia, Samoa',
      'Taiohae, French Polynesia',
      'Papeete, French Polynesia',
      'Rikitea, French Polynesia',
      'Adamstown, Pitcairn',
      'San Juan Bautista, Chile',
      'Puntarenas, Costa Rica',
      'El Porvenir, Panama',
      'Great Corn Island, Nicaragua',
      'Roatan, Honduras',
      'Hamilton, Bermuda',
      'Longyearbyen, Norway',
      'Bjørnøya, Norway',
      'Newport, England',
      'Lundy, England',
      'St. Mary\'s, England',
      'Llangefni, Wales',
      'St. Kilda, Scotland',
      'Fair Isle, Scotland',
      'Fernando de Noronha, Brazil',
      'Tristan da Cunha',
      'Saint Helena',
      'Ascension Island',
      'Falkland Islands',
      'South Georgia',
      'Antarctica',
      'Nagurskoye, Russia',
      'Pevek, Russia',
      'Yuzhno-Sakhalinsk, Russia',
      'Yuzhno-Kurilsk, Russia',
      'Sapporo, Japan',
      'Naha, Japan',
      'Jeju, South Korea',
      'Haikou, China',
      'Taipei, Taiwan',
      'Macau, China',
      'Bandar Seri Begawan, Brunei',
      'Dili, East Timor',
      'Koror, Palau',
      'Palikir, Micronesia',
      'Majuro, Marshall Islands',
      'Tarawa, Kiribati',
      'Yaren, Nauru',
      'Funafuti, Tuvalu',
      'Alofi, Niue',
      'Fakaofo, Tokelau',
      'Mata-Utu, Wallis and Futuna',
      'Pago Pago, American Samoa',
      'Hagatna, Guam',
      'Saipan, Northern Mariana Islands',
      'Wake Island, USA',
      'Midway Atoll, USA',
      'Johnston Atoll, USA',
      'Baker Island, USA',
      'Howland Island, USA',
      'Jarvis Island, USA',
      'Kingman Reef, USA',
      'Palmyra Atoll, USA'
    ]
  }
}; 
